import { Head, Link, useForm } from '@inertiajs/react'
import Layout from '../../components/Layout'

interface TeamOption {
  id: number
  name: string
}

interface Props {
  teams: TeamOption[]
  preselected_team_id: number | null
}

const MEETING_TYPES = [
  { value: 'weekly_tactical', label: 'Weekly Tactical', description: 'Standard 7-section Pinnacle meeting' },
  { value: 'monthly_strategic', label: 'Monthly Strategic', description: 'Longer-form strategy review' },
  { value: 'annual_planning', label: 'Annual Planning', description: 'Full-day planning session' },
  { value: 'custom', label: 'Custom', description: 'Build your own agenda from scratch' },
]

export default function AgendaTemplatesNew({ teams, preselected_team_id }: Props) {
  const { data, setData, post, processing, errors } = useForm({
    name: '',
    description: '',
    meeting_type: 'weekly_tactical',
    team_ids: preselected_team_id ? [preselected_team_id] : [] as number[],
  })

  const toggleTeam = (id: number) => {
    setData('team_ids', data.team_ids.includes(id)
      ? data.team_ids.filter(t => t !== id)
      : [...data.team_ids, id]
    )
  }

  const handleSubmit = () => {
    post('/agenda_templates')
  }

  return (
    <Layout>
      <Head title="New Agenda Template — Pinnacle" />
      <div className="max-w-2xl mx-auto px-6 py-8">

        <nav className="text-sm mb-6 text-text-muted">
          <Link href="/agenda_templates" className="hover:text-text-primary">Agendas</Link>
          <span className="mx-2">›</span>
          <span className="text-text-primary">New Template</span>
        </nav>

        <h1 className="text-2xl font-semibold text-text-primary font-heading mb-6">Create Agenda Template</h1>

        <div className="bg-white rounded-lg border border-stone shadow-sm p-6 space-y-6">

          <div>
            <label className="block text-sm font-bold uppercase tracking-widest text-text-primary mb-2">
              Template Name <span className="text-pinnacle-red">*</span>
            </label>
            <input
              type="text"
              value={data.name}
              onChange={e => setData('name', e.target.value)}
              placeholder="e.g. Weekly Tactical — Leadership"
              className="w-full px-3 py-2 border border-stone rounded text-sm focus:border-charcoal focus:outline-none"
              autoFocus
            />
            {errors.name && <p className="text-pinnacle-red text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-bold uppercase tracking-widest text-text-primary mb-2">
              Description
            </label>
            <textarea
              value={data.description}
              onChange={e => setData('description', e.target.value)}
              placeholder="What is this agenda used for?"
              rows={2}
              className="w-full px-3 py-2 border border-stone rounded text-sm focus:border-charcoal focus:outline-none resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-bold uppercase tracking-widest text-text-primary mb-3">
              Meeting Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              {MEETING_TYPES.map(type => (
                <label
                  key={type.value}
                  className={[
                    'border rounded-lg p-3 cursor-pointer transition-colors',
                    data.meeting_type === type.value
                      ? 'border-pinnacle-red bg-[rgba(237,28,36,0.15)]'
                      : 'border-stone hover:border-charcoal'
                  ].join(' ')}
                >
                  <input
                    type="radio"
                    className="hidden"
                    checked={data.meeting_type === type.value}
                    onChange={() => setData('meeting_type', type.value)}
                  />
                  <p className="font-semibold text-sm text-text-primary">{type.label}</p>
                  <p className="text-sm text-text-secondary mt-0.5">{type.description}</p>
                </label>
              ))}
            </div>
          </div>

          {teams.length > 0 && (
            <div>
              <label className="block text-sm font-bold uppercase tracking-widest text-text-primary mb-3">
                Assign to Teams
              </label>
              <div className="space-y-2">
                {teams.map(team => (
                  <label key={team.id} className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={data.team_ids.includes(team.id)}
                      onChange={() => toggleTeam(team.id)}
                      className="w-4 h-4 rounded border-stone text-pinnacle-red focus:ring-pinnacle-red"
                    />
                    <span className="text-sm font-medium text-text-primary group-hover:underline transition-colors">
                      {team.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

        </div>

        <p className="text-sm text-text-muted mt-4">
          Default Pinnacle sections will be created automatically. You can customize them after creation.
        </p>

        <div className="flex justify-end gap-3 mt-6">
          <Link
            href="/agenda_templates"
            className="px-4 py-2 text-sm font-medium text-text-primary bg-white border border-stone rounded hover:bg-[#F8F9FA] transition-colors"
          >
            Cancel
          </Link>
          <button
            onClick={handleSubmit}
            disabled={processing || !data.name.trim()}
            className="px-5 py-2 text-sm font-semibold text-white bg-pinnacle-red rounded hover:bg-pinnacle-red-dark transition-colors disabled:opacity-40"
          >
            {processing ? 'Creating...' : 'Create Template'}
          </button>
        </div>
      </div>
    </Layout>
  )
}
